
import * as React from "react"
import { useIsMobile } from "@/hooks/use-mobile"

const SIDEBAR_COOKIE_NAME = "sidebar:state"
const SIDEBAR_DISPLAY_MODE_COOKIE = "sidebar:display-mode"
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
const SIDEBAR_WIDTH = "12rem"
const SIDEBAR_WIDTH_MOBILE = "14rem"
const SIDEBAR_WIDTH_MINI = "4rem"
const SIDEBAR_WIDTH_ICON = "3rem"
const SIDEBAR_KEYBOARD_SHORTCUT = "b"

type SidebarDisplayMode = "expanded" | "mini"

type SidebarContext = {
  state: "expanded" | "collapsed"
  displayMode: SidebarDisplayMode
  setDisplayMode: (mode: SidebarDisplayMode) => void
  open: boolean
  setOpen: (open: boolean) => void
  openMobile: boolean
  setOpenMobile: (open: boolean) => void
  isMobile: boolean
  toggleSidebar: () => void
  cycleSidebar: () => void
}

const SidebarContext = React.createContext<SidebarContext | null>(null)

export function useSidebar() {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.")
  }
  return context
}

export const SidebarProvider = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    defaultOpen?: boolean
    open?: boolean
    onOpenChange?: (open: boolean) => void
    defaultDisplayMode?: SidebarDisplayMode
  }
>(
  (
    {
      defaultOpen = true,
      open: openProp,
      onOpenChange: setOpenProp,
      defaultDisplayMode = "expanded",
      className,
      style,
      children,
      ...props
    },
    ref
  ) => {
    const isMobile = useIsMobile()
    const [openMobile, setOpenMobile] = React.useState(false)
    const [_open, _setOpen] = React.useState(defaultOpen)
    const [displayMode, setDisplayModeState] = React.useState<SidebarDisplayMode>(() => {
      if (typeof window !== "undefined") {
        const saved = localStorage.getItem(SIDEBAR_DISPLAY_MODE_COOKIE)
        return (saved as SidebarDisplayMode) || defaultDisplayMode
      }
      return defaultDisplayMode
    })
    const open = openProp ?? _open
    
    const setOpen = React.useCallback(
      (value: boolean | ((value: boolean) => boolean)) => {
        const openState = typeof value === "function" ? value(open) : value
        if (setOpenProp) {
          setOpenProp(openState)
        } else {
          _setOpen(openState)
        }
        document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
      },
      [setOpenProp, open]
    )

    const setDisplayMode = React.useCallback(
      (mode: SidebarDisplayMode) => {
        setDisplayModeState(mode)
        if (typeof window !== "undefined") {
          localStorage.setItem(SIDEBAR_DISPLAY_MODE_COOKIE, mode)
        }
      },
      []
    )

    const toggleSidebar = React.useCallback(() => {
      return isMobile
        ? setOpenMobile((open) => !open)
        : setOpen((open) => !open)
    }, [isMobile, setOpen, setOpenMobile])

    const cycleSidebar = React.useCallback(() => {
      if (isMobile) {
        setOpenMobile((open) => !open)
      } else {
        if (open && displayMode === "expanded") {
          setDisplayMode("mini")
        } else if (open && displayMode === "mini") {
          setOpen(false)
        } else {
          setOpen(true)
          setDisplayMode("expanded")
        }
      }
    }, [isMobile, open, displayMode, setOpen, setDisplayMode, setOpenMobile])

    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (
          event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
          (event.metaKey || event.ctrlKey)
        ) {
          event.preventDefault()
          cycleSidebar()
        }
      }

      window.addEventListener("keydown", handleKeyDown)
      return () => window.removeEventListener("keydown", handleKeyDown)
    }, [cycleSidebar])

    const state = React.useMemo<"expanded" | "collapsed">(() => {
      return open ? "expanded" : "collapsed"
    }, [open])

    const contextValue = React.useMemo<SidebarContext>(
      () => ({
        state,
        displayMode,
        setDisplayMode,
        open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
        cycleSidebar,
      }),
      [state, displayMode, setDisplayMode, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar, cycleSidebar]
    )

    return (
      <SidebarContext.Provider value={contextValue}>
        <div
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH,
              "--sidebar-width-mini": SIDEBAR_WIDTH_MINI,
              "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          className={className}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      </SidebarContext.Provider>
    )
  }
)
SidebarProvider.displayName = "SidebarProvider"

export const SIDEBAR_CONSTANTS = {
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_MOBILE,
  SIDEBAR_WIDTH_MINI,
  SIDEBAR_WIDTH_ICON,
}

export type { SidebarDisplayMode }
